import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  FaComments,
  FaTools,
  FaHandsHelping,
  FaUserShield,
  FaDatabase,
  FaUserTie,
  FaCarAlt,
  FaPills,
  FaLock
} from 'react-icons/fa';
import { MdOutlineFeedback, MdBuild, MdSupportAgent } from 'react-icons/md';
import { GiMedicines } from 'react-icons/gi';
import { HiOutlineDocumentReport } from 'react-icons/hi';

const ReportAll: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const reportOptions = [
    {
      title: 'Feedback & Complaints',
      description: 'Submit feedback, complaints, or suggestions for Ultra Care Services.',
      icon: <MdOutlineFeedback size={isSmallScreen ? 28 : 36} color="#7b1fa2" />,
      bgColor: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
      route: '/feedback-complaints',
      action: 'Provide Feedback'
    },
    {
      title: 'Maintenance Request',
      description: 'Report maintenance issues for participant homes, offices, or vehicles.',
      icon: <MdBuild size={isSmallScreen ? 28 : 36} color="#00695c" />,
      bgColor: 'linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)',
      route: '/maintenance',
      action: 'Request Service'
    },
    {
      title: 'Outreach Services',
      description: 'Document outreach services provided to clients.',
      icon: <MdSupportAgent size={isSmallScreen ? 28 : 36} color="#283593" />,
      bgColor: 'linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)',
      route: '/outreach',
      action: 'Log Outreach'
    },
    {
      title: 'Client Incident',
      description: 'Report incidents involving clients in care.',
      icon: <FaUserShield size={isSmallScreen ? 28 : 36} color="#5d4037" />,
      bgColor: 'linear-gradient(135deg, #efebe9 0%, #d7ccc8 100%)',
      route: '/Client-Incident',
      action: 'Report Incident'
    },
    {
      title: 'Data Breach',
      description: 'Report potential or actual data privacy breaches.',
      icon: <FaDatabase size={isSmallScreen ? 28 : 36} color="#607d8b" />,
      bgColor: 'linear-gradient(135deg, #eceff1 0%, #cfd8dc 100%)',
      route: '/dataBreach-Incident',
      action: 'Report Breach'
    },
    {
      title: 'Employee Incident',
      description: 'Report workplace incidents involving staff.',
      icon: <FaUserTie size={isSmallScreen ? 28 : 36} color="#795548" />,
      bgColor: 'linear-gradient(135deg, #efebe9 0%, #d7ccc8 100%)',
      route: '/employee-IncidentReport',
      action: 'File Report'
    },
    {
      title: 'Equipment Incident',
      description: 'Report equipment malfunctions or failures.',
      icon: <FaTools size={isSmallScreen ? 28 : 36} color="#8d6e63" />,
      bgColor: 'linear-gradient(135deg, #efebe9 0%, #d7ccc8 100%)',
      route: '/equipment-IncidentReport',
      action: 'Report Issue'
    },
    {
      title: 'Medication Report',
      description: 'Document medication administration issues.',
      icon: <GiMedicines size={isSmallScreen ? 28 : 36} color="#e91e63" />,
      bgColor: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)',
      route: '/medication-IncidentReport',
      action: 'Log Incident'
    },
    {
      title: 'Restrictive Practices',
      description: 'Report use of restrictive practices in care.',
      icon: <FaLock size={isSmallScreen ? 28 : 36} color="#3f51b5" />,
      bgColor: 'linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)',
      route: '/restrictive-PracticesReport',
      action: 'Document Use'
    }
  ];

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        mx: 'auto',
        maxWidth: '1400px',
        minHeight: '100vh',
        bgcolor: '#fafafa'
      }}
    >
      <Box
        sx={{
          mb: { xs: 3, sm: 4, md: 5 },
          textAlign: 'center',
          px: { xs: 1, sm: 2 }
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            mb: 2,
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
            color: theme.palette.primary.main,
            lineHeight: 1.2
          }}
        >
          Incident Reporting Portal
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            maxWidth: '800px',
            mx: 'auto',
            color: theme.palette.text.secondary,
            fontSize: { xs: '0.875rem', sm: '1.1rem' }
          }}
        >
          Select the appropriate form to document incidents, request services, or provide feedback
        </Typography>
      </Box>

      <Grid container spacing={isSmallScreen ? 2 : 3}>
        {reportOptions.map((option, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: isSmallScreen ? 1.5 : 2,
                textAlign: 'center',
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                background: option.bgColor,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.12)'
                }
              }}
            >
              <Box
                sx={{
                  mb: isSmallScreen ? 1.5 : 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: isSmallScreen ? 56 : 72,
                  height: isSmallScreen ? 56 : 72,
                  mx: 'auto',
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  boxShadow: 1
                }}
              >
                {option.icon}
              </Box>
              <CardContent
                sx={{ flexGrow: 1, p: isSmallScreen ? '8px !important' : '16px !important' }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    fontSize: isSmallScreen ? '1rem' : '1.1rem',
                    color: theme.palette.text.primary,
                    lineHeight: 1.3
                  }}
                >
                  {option.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    fontSize: isSmallScreen ? '0.8125rem' : '0.875rem',
                    color: theme.palette.text.secondary,
                    lineHeight: 1.5
                  }}
                >
                  {option.description}
                </Typography>
              </CardContent>
              <Box sx={{ mt: 'auto', px: isSmallScreen ? 1 : 2, pb: isSmallScreen ? 1 : 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate(option.route)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1,
                    py: 1,
                    fontSize: isSmallScreen ? '0.8125rem' : '0.875rem',
                    fontWeight: 500,
                    boxShadow: 'none',
                    '&:hover': {
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }
                  }}
                >
                  {option.action}
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ReportAll;
